import {useState} from 'react'
import {TextAreaApi, TextAreaProps, TextAreaValidationType} from './types'

type UseActionsProps = Pick<
  TextAreaProps,
  | 'onSubmit'
  | 'onCancel'
  | 'onBlur'
  | 'onChange'
  | 'onPaste'
  | 'onFocus'
  | 'onInit'
  | 'paddings'
  | 'allowNewLine'
  | 'isValid'
  | 'allowSoftNewLine'
  | 'stopEnterPropagation'
> & {resetValue: (value?: string) => void}

interface RetType {
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  onFocus: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onPaste: (text: string) => void
  ref: (el: HTMLTextAreaElement) => void
  validationType: TextAreaValidationType
}

export const useActions = (props: UseActionsProps): RetType => {
  const [baseElement, setBaseElement] = useState<HTMLTextAreaElement>()
  const [validationType, setValidationType] =
    useState<TextAreaValidationType>('empty')
  const {allowNewLine, allowSoftNewLine, stopEnterPropagation} = props

  const api: TextAreaApi = {
    baseElement,
    text: '',
    resetValue: (value = '') => {
      if (baseElement) {
        baseElement.value = value
      }
      props.resetValue(value)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    api.text = e.currentTarget.value

    if (e.key === 'Enter') {
      const isNewLine = allowNewLine || (allowSoftNewLine && e.shiftKey)
      if (stopEnterPropagation) {
        e.stopPropagation()
      }
      if (!isNewLine) {
        e.stopPropagation()
        e.preventDefault()
        props.onSubmit?.(api)
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation()
      e.preventDefault()
      props.onCancel?.(api)
    }
  }

  const onFocus = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    api.text = e.currentTarget.value

    const length = e.target?.value?.length
    if (length) {
      e.target?.setSelectionRange(length, length)
    }

    props.onFocus?.(api)
  }

  const onBlur = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    api.text = e.currentTarget.value
    props.onBlur?.(api)
  }

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    api.text = e.currentTarget.value
    props.onChange?.(api)
    props.isValid && setValidationType(props.isValid(api.text))
  }

  const onPaste = (text: string): void => {
    api.text = text
    props.onPaste?.(api)
    props.isValid && setValidationType(props.isValid(api.text))
  }

  const ref = (el: HTMLTextAreaElement): void => {
    setBaseElement(el)
    props.onInit?.(el)
    props.isValid && setValidationType(props.isValid(api.text))
  }

  return {onKeyDown, onFocus, onBlur, onChange, onPaste, ref, validationType}
}
